import { Pipe, PipeTransform } from '@angular/core';
import { Field } from '../types/field.types';

@Pipe({
  name: 'checkDependent',
  pure: false
})
export class CheckDependentPipe implements PipeTransform {

  transform(value: any, field: Field, requestPayload: any, languageKey: string, formTemplateList?: any): any {
    if (!field) {
      return false;
    }
    
    // Evaluate if the field/section itself is dependent and satisfied
    if (field.isDependant) {
      return this.evaluateDependency(field, requestPayload, languageKey, formTemplateList);
    }
    
    return false;
  }

  evaluateDependency(field: any, requestPayload: any, languageKey: string, formTemplateList: any): boolean {
    if (field.isDependant && field.dependOn && field.dependOn.bindKey) {
      // 1. Check if the parent triggering field is visible in the layout hierarchy
      const isParentVisible = this.isFieldVisible(field.dependOn.bindKey, formTemplateList, requestPayload, languageKey);
      if (!isParentVisible) {
        return false;
      }

      // 2. Check if the parent field value satisfies the dependency condition
      const bindKey = field.dependOn.bindKey;
      const compId = field.dependOn.componentId;
      let parentVal = requestPayload ? requestPayload[bindKey] : undefined;
      
      if (parentVal === undefined || parentVal === null) {
        if (compId && requestPayload) {
          parentVal = requestPayload[compId];
        }
      }

      if (parentVal === undefined || parentVal === null || parentVal === '') {
        if (parentVal !== false) {
          return false;
        }
      }

      if (parentVal instanceof Array) {
        return field.dependOn.value.some(aValue => parentVal.includes(aValue));
      } else {
        const parentStr = String(parentVal).trim().toLowerCase();
        return field.dependOn.value.some(v => String(v).trim().toLowerCase() === parentStr);
      }
    }
    return true;
  }

  isFieldVisible(bindKey: string, formTemplateList: any, requestPayload: any, languageKey: string): boolean {
    if (!formTemplateList) {
      return true; // Fallback if template list is not passed
    }

    let targetField: any = null;
    let parentContainers: any[] = [];

    const search = (fields: any[], parents: any[]) => {
      if (!fields || targetField) return;
      for (const f of fields) {
        if (f.bindKey === bindKey) {
          targetField = f;
          parentContainers = [...parents];
          return;
        }
        if (f.columns) {
          f.columns.forEach(col => search(col.model || [], [...parents, f]));
        }
        if (f.fieldList) {
          search(f.fieldList || [], [...parents, f]);
        }
      }
    };

    const searchTemplate = (template: any) => {
      if (!template) return;
      if (Array.isArray(template)) {
        template.forEach(item => {
          if (item.type === 'stepper') {
            if (item.steps) {
              item.steps.forEach(step => search(step.model || [], [item, step]));
            }
          } else {
            if (item.columns) {
              item.columns.forEach(col => search(col.model || [], [item]));
            }
            if (item.model) {
              search(item.model || [], [item]);
            }
          }
        });
      } else {
        if (template.steps) {
          template.steps.forEach(step => search(step.model || [], [template, step]));
        } else if (template.columns) {
          template.columns.forEach(col => search(col.model || [], [template]));
        } else if (template.model) {
          search(template.model || [], [template]);
        }
      }
    };

    searchTemplate(formTemplateList);

    if (!targetField) {
      return true; // If not found in current template schema context, assume visible
    }

    // Check if any of the nesting sections / step containers are hidden
    for (const container of parentContainers) {
      if (container.isDependant) {
        const isContainerVisible = this.evaluateDependency(container, requestPayload, languageKey, formTemplateList);
        if (!isContainerVisible) {
          return false;
        }
      }
    }

    // Check if the target field itself is hidden
    if (targetField.isDependant) {
      return this.evaluateDependency(targetField, requestPayload, languageKey, formTemplateList);
    }

    return true;
  }
}
